// Headless bots-only rounds (§30.3, §30.4): every active slot driven by a BotManager brain and
// stepped in server mode as fast as possible, with no network or timers. `simHash` is the
// Node/Chrome determinism probe (`__SHOVEOFF__.simHash`); the bot harness builds on `runBotsOnly`.
import { MAX_BEANS, NO_SLOT } from '../constants';
import { Difficulty, ItemKind, MapId, ModeId, MutatorId, type Personality } from '../ids';
import { BOT_NAMES } from '../names';
import { stepWorld } from '../sim/step';
import { createWorld, hashWorld } from '../sim/world';
import type { InputFrame, RoundConfig, StepContext, World } from '../types';
import type { BotContext } from './botTypes';
import { BotManager } from './manager';

const PERSONALITY_COUNT = 5;

/** An FFA round config with `beans` active slots, items on and no mutator. */
export function botsOnlyConfig(seed: number, mapId: MapId, modeId: ModeId, beans: number = MAX_BEANS): RoundConfig {
  return {
    mapId, modeId, mutator: MutatorId.NONE, seed, golden: false, itemsOn: true, teamsOn: false,
    activeSlots: Array.from({ length: MAX_BEANS }, (_, i) => i < beans),
    teams: new Array<number>(MAX_BEANS).fill(0),
    matchScores: new Array<number>(MAX_BEANS).fill(0),
    crownedSlot: NO_SLOT,
    startItems: new Array<ItemKind>(MAX_BEANS).fill(ItemKind.NONE),
  };
}

/**
 * A BotManager with a brain for every active slot of `cfg`: personalities by slot, seeds derived
 * from the round seed and slot. Call `reset(world)` before the first step.
 */
export function botsOnlyManager(cfg: RoundConfig, difficulty: Difficulty = Difficulty.NORMAL): BotManager {
  const bots = new BotManager();
  for (let slot = 0; slot < MAX_BEANS; slot++) {
    if (!cfg.activeSlots[slot]) continue;
    bots.setBot(slot, {
      slot, personality: (slot % PERSONALITY_COUNT) as Personality, difficulty,
      seed: (cfg.seed + slot + 1) >>> 0, name: BOT_NAMES[slot % BOT_NAMES.length],
    });
  }
  return bots;
}

/**
 * Runs `ticks` server-mode steps of a bots-only round from `cfg` and returns the world. `onTick`
 * sees the world (with that tick's events) after every step; events are cleared before the next
 * step.
 */
export function runBotsOnly(cfg: RoundConfig, ticks: number, onTick?: (w: World) => void): World {
  const w = createWorld(cfg, 1, false);
  const bots = botsOnlyManager(cfg);
  bots.reset(w);
  const inputs: (InputFrame | null)[] = new Array<InputFrame | null>(MAX_BEANS).fill(null);
  const ctx: StepContext = { predictSlot: NO_SLOT, rewindTicks: new Array<number>(MAX_BEANS).fill(0) };
  const botCtx: BotContext = { matchScores: cfg.matchScores, crowned: cfg.crownedSlot, humanSlots: [], reactionMult: 1, teamsOn: cfg.teamsOn };
  for (let i = 0; i < ticks; i++) {
    bots.inputsFor(w, botCtx, inputs);
    stepWorld(w, inputs, ctx);
    bots.observe(w);
    onTick?.(w);
    w.events.length = 0;
  }
  return w;
}

function isId<T extends number>(table: Readonly<Record<string, T>>, v: number): v is T {
  return Object.values(table).includes(v as T);
}

/**
 * `hashWorld` after `ticks` steps of an 8-bot round; identical in Node and Chrome (§30.4). Takes
 * plain numbers because it is also the `__SHOVEOFF__.simHash` page hook; unknown ids throw.
 */
export function simHash(seed: number, ticks: number, mapId: number, modeId: number): number {
  if (!isId(MapId, mapId) || !isId(ModeId, modeId)) throw new RangeError(`simHash: bad map ${mapId} or mode ${modeId}`);
  return hashWorld(runBotsOnly(botsOnlyConfig(seed >>> 0, mapId, modeId), ticks));
}
